import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress,
  InputAdornment, IconButton, Stepper, Step, StepLabel, Chip,
  LinearProgress,
} from '@mui/material';
import {
  Visibility, VisibilityOff, Restaurant, Lock, Person, Business,
  Email, Phone, Store, CheckCircle, ArrowBack, ArrowForward,
  DarkMode, LightMode, CelebrationOutlined, Message, VerifiedUser,
  Refresh,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useThemeModeContext } from '../hooks/useThemeMode';
import { food, neu } from '../theme';
import apiService from '../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Step1Data {
  restaurantName: string;
  restaurantCode: string;
  address: string;
  phone: string;
  email: string;
}

interface Step2Data {
  firstName: string;
  lastName: string;
  adminEmail: string;
  username: string;
  password: string;
  confirmPassword: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function generateCode(name: string): string {
  return name
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 6)
    .padEnd(4, 'X');
}

function passwordStrength(pw: string): { score: number; label: string; color: string } {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const map = [
    { label: 'Weak',      color: '#ef4444' },
    { label: 'Fair',      color: '#f97316' },
    { label: 'Good',      color: '#eab308' },
    { label: 'Strong',    color: '#22c55e' },
    { label: 'Very Strong', color: '#10b981' },
  ];
  return { score, ...map[score] };
}

// ── Component ─────────────────────────────────────────────────────────────────
const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toggleMode, isDark } = useThemeModeContext();
  const n = isDark ? neu.dark : neu.light;

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [step1, setStep1] = useState<Step1Data>({
    restaurantName: '',
    restaurantCode: '',
    address: '',
    phone: '',
    email: '',
  });

  const [step2, setStep2] = useState<Step2Data>({
    firstName: '',
    lastName: '',
    adminEmail: '',
    username: '',
    password: '',
    confirmPassword: '',
  });

  // OTP Verification States
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [timerActive, setTimerActive] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [createdRestaurantCode, setCreatedRestaurantCode] = useState('');

  const glassBg     = isDark ? 'rgba(28,24,22,0.82)' : 'rgba(255,255,255,0.88)';
  const glassBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)';

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px',
      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
      backdropFilter: 'blur(12px)',
      '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(200,185,170,0.35)' },
      '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(200,185,170,0.6)' },
      '&.Mui-focused fieldset': { borderColor: food.red, borderWidth: '1.5px' },
      '&.Mui-focused': { boxShadow: `0 0 0 3px ${food.redSoft}` },
    },
    '& .MuiInputLabel-root': { color: n.textSecondary, fontWeight: 500 },
    '& .MuiInputLabel-root.Mui-focused': { color: food.red },
    '& .MuiOutlinedInput-input': { color: n.textPrimary },
    '& .MuiInputAdornment-root svg': { color: n.textMuted },
  };

  // OTP Countdown timer
  useEffect(() => {
    let interval: any = null;
    if (timerActive && countdown > 0) {
      interval = setInterval(() => {
        setCountdown(c => c - 1);
      }, 1000);
    } else if (countdown === 0) {
      setTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [timerActive, countdown]);

  // ── Step 0 handlers ──────────────────────────────────────────────────────
  const handleNameChange = (name: string) => {
    setStep1(s => ({
      ...s,
      restaurantName: name,
      restaurantCode: generateCode(name),
    }));
  };

  const validateStep1 = (): string => {
    if (!step1.restaurantName.trim()) return 'Restaurant name is required';
    if (!step1.restaurantCode.trim()) return 'Restaurant code is required';
    if (step1.restaurantCode.length < 3) return 'Restaurant code must be at least 3 characters';
    if (!step1.phone.trim()) return 'Phone number is required';
    return '';
  };

  const handleNextToOtp = async () => {
    setError('');
    const err = validateStep1();
    if (err) { setError(err); return; }

    setOtpLoading(true);
    try {
      const res = await apiService.sendWhatsAppOtp(step1.phone);
      if (res.success) {
        setActiveStep(1);
        setCountdown(60);
        setTimerActive(true);
        setOtp('');
      } else {
        setError(res.message || 'Failed to send OTP via WhatsApp. Please check your number.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Error communicating with WhatsApp OTP service.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setOtpLoading(true);
    try {
      const res = await apiService.sendWhatsAppOtp(step1.phone);
      if (res.success) {
        setCountdown(60);
        setTimerActive(true);
        setOtp('');
      } else {
        setError(res.message || 'Failed to resend OTP via WhatsApp.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Error resending OTP.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (enteredOtp: string) => {
    if (enteredOtp.length !== 6) return;

    setError('');
    setOtpLoading(true);
    try {
      const res = await apiService.verifyWhatsAppOtp(step1.phone, enteredOtp);
      if (res.verified) {
        setOtpVerified(true);
        setTimerActive(false);
        setTimeout(() => {
          setActiveStep(2);
        }, 800);
      } else {
        setError(res.message || 'Invalid or expired OTP. Please try again.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'OTP verification failed.');
    } finally {
      setOtpLoading(false);
    }
  };

  const validateStep2 = (): string => {
    if (!step2.firstName.trim()) return 'First name is required';
    if (!step2.lastName.trim())  return 'Last name is required';
    if (!step2.username.trim() || step2.username.length < 4) return 'Username must be at least 4 characters';
    if (!step2.adminEmail.trim() || !step2.adminEmail.includes('@')) return 'Valid admin email is required';
    if (step2.password.length < 8) return 'Password must be at least 8 characters';
    if (step2.password !== step2.confirmPassword) return 'Passwords do not match';
    return '';
  };

  const handleBackToStep0 = () => {
    setError('');
    setActiveStep(0);
  };

  const handleBackToOtp = () => {
    setError('');
    setActiveStep(1);
  };

  // ── Submit (Step 2) ───────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const err = validateStep2();
    if (err) { setError(err); return; }

    setLoading(true);
    try {
      // 1. Find Demo Plan (once-per-lifetime — only used at signup)
      let demoPlanId: string | undefined;
      try {
        const plans = await apiService.getAllPricingPlans();
        const demo = plans.find(
          p => p.name.toLowerCase().includes('demo') && p.isActive
        );
        if (demo) demoPlanId = demo.id;
      } catch {
        // Plans unavailable — proceed without planId
      }

      // 2. Create Tenant with 7-day Demo expiry
      const planExpiry = new Date();
      planExpiry.setDate(planExpiry.getDate() + 7);

      const restaurantCode = step1.restaurantCode.toUpperCase().replace(/[^A-Z0-9]/g, '');

      const tenant = await apiService.createTenant({
        name: step1.restaurantName,
        subdomain: restaurantCode.toLowerCase(),
        restaurantCode: restaurantCode,
        planId: demoPlanId,
        planExpiry: planExpiry.toISOString(),
      });

      // 3. Create Restaurant under tenant
      await apiService.createRestaurant({
        tenantId: tenant.id,
        name: step1.restaurantName,
        restaurantCode: restaurantCode,
        address: step1.address || '',
        phone: step1.phone,
        email: step2.adminEmail,
        isActive: true,
        currency: 'INR',
        timeZone: 'Asia/Kolkata',
      } as any);

      // 4. Create Admin User
      await apiService.createUser({
        tenantId: tenant.id,
        username: step2.username,
        email: step2.adminEmail,
        password: step2.password,
        firstName: step2.firstName,
        lastName: step2.lastName,
        role: 'Admin',
      });

      // 5. Auto-login
      setCreatedRestaurantCode(restaurantCode);
      const success = await login(restaurantCode, step2.username, step2.password);
      if (success) {
        setActiveStep(3); // Show success step
      } else {
        // Login failed but account was created — show success with manual login hint
        setActiveStep(3);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.title ||
        err?.message ||
        'Registration failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Password strength bar ─────────────────────────────────────────────────
  const pwStrength = passwordStrength(step2.password);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{
      minHeight: '100vh',
      background: isDark ? food.darkBg : food.bg,
      backgroundImage: isDark
        ? `radial-gradient(ellipse 70% 55% at 0% 0%, rgba(224,42,42,0.07) 0%, transparent 70%),
           radial-gradient(ellipse 70% 55% at 100% 100%, rgba(56,142,60,0.05) 0%, transparent 70%)`
        : `radial-gradient(ellipse 70% 55% at 0% 0%, ${food.bgPeach} 0%, transparent 70%),
           radial-gradient(ellipse 70% 55% at 100% 100%, ${food.bgMint} 0%, transparent 70%)`,
      backgroundAttachment: 'fixed',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: { xs: 2, md: 4 }, position: 'relative', overflow: 'hidden',
    }}>

      {/* Floating blobs */}
      <Box sx={{ position: 'fixed', top: '8%', left: '5%', width: 220, height: 220, borderRadius: '50%', background: isDark ? 'rgba(224,42,42,0.06)' : food.bgPeach, filter: 'blur(48px)', pointerEvents: 'none', animation: 'float-slow 9s ease-in-out infinite' }} />
      <Box sx={{ position: 'fixed', bottom: '10%', right: '6%', width: 280, height: 280, borderRadius: '50%', background: isDark ? 'rgba(56,142,60,0.05)' : food.bgMint, filter: 'blur(56px)', pointerEvents: 'none', animation: 'float-medium 7s ease-in-out infinite' }} />

      {/* Theme toggle */}
      <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 10 }}>
        <IconButton onClick={toggleMode} sx={{ background: glassBg, backdropFilter: 'blur(16px)', border: `1px solid ${glassBorder}`, boxShadow: '0 4px 16px rgba(26,18,9,0.08)', color: n.textSecondary, borderRadius: '14px', '&:hover': { transform: 'translateY(-1px)' } }}>
          {isDark ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, maxWidth: 1080, width: '100%', alignItems: 'stretch' }}>

        {/* ── Left Panel ──────────────────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: [0.16,1,0.3,1] }} style={{ flex: 1, display: 'flex' }}>
          <Box sx={{
            flex: 1, p: { xs: 3, md: 5 }, borderRadius: '32px',
            background: isDark
              ? 'linear-gradient(145deg, rgba(224,42,42,0.85) 0%, rgba(180,24,24,0.95) 60%, rgba(26,18,9,0.98) 100%)'
              : 'linear-gradient(145deg, #E02A2A 0%, #C62020 55%, #8B1111 100%)',
            boxShadow: `0 20px 60px ${food.redGlow}, 0 4px 16px rgba(26,18,9,0.15)`,
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            minHeight: 480, position: 'relative', overflow: 'hidden',
          }}>
            {/* Decorative blobs */}
            <Box sx={{ position: 'absolute', top: -60, right: -60, width: 240, height: 240, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', filter: 'blur(1px)', pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', bottom: -80, left: -40, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />

            {/* Brand */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                <Box sx={{ width: 50, height: 50, borderRadius: '16px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Restaurant sx={{ color: '#fff', fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800, fontFamily: 'Space Grotesk', letterSpacing: '-0.02em', lineHeight: 1.1 }}>MenuMaster</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem', letterSpacing: '0.05em' }}>RESTAURANT PLATFORM</Typography>
                </Box>
              </Box>

              <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, fontFamily: 'Space Grotesk', letterSpacing: '-0.03em', lineHeight: 1.12, mb: 2 }}>
                Start your<br />free 7-day trial
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '1rem', lineHeight: 1.65, maxWidth: 340 }}>
                Set up your restaurant in minutes. No credit card required.
              </Typography>

              {/* Trial benefits */}
              <Box sx={{ mt: 3.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  '✓  WhatsApp-verified Secure Sign-Up',
                  '✓  Full POS & Kitchen Display System',
                  '✓  Table & Order Management',
                  '✓  AI Food Assistant (Maya)',
                  '✓  Staff & Inventory tracking',
                  '✓  7 days free — upgrade anytime',
                ].map(item => (
                  <Typography key={item} sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.88rem', fontWeight: 500 }}>{item}</Typography>
                ))}
              </Box>
            </Box>

            {/* Demo plan notice */}
            <Box sx={{ mt: 3, p: 2, borderRadius: '16px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.78rem', fontWeight: 600 }}>
                🎁 Demo Plan — One-time offer
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem', mt: 0.5 }}>
                The Demo plan is available once per restaurant. After the 7-day trial, choose a paid plan to continue.
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* ── Right Panel: Form ────────────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease: [0.16,1,0.3,1], delay: 0.1 }} style={{ flex: 1, display: 'flex' }}>
          <Box sx={{
            flex: 1, p: { xs: 3, md: 4.5 }, borderRadius: '32px',
            background: glassBg, backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)',
            border: `1px solid ${glassBorder}`,
            boxShadow: isDark
              ? '0 16px 48px rgba(0,0,0,0.45), 0 4px 16px rgba(0,0,0,0.3)'
              : '0 16px 48px rgba(26,18,9,0.10), 0 4px 16px rgba(26,18,9,0.05)',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>

            {/* Stepper */}
            {activeStep < 3 && (
              <Box sx={{ mb: 4 }}>
                <Stepper activeStep={activeStep} sx={{
                  '& .MuiStepLabel-label': { color: n.textMuted, fontWeight: 600, fontSize: '0.8rem' },
                  '& .MuiStepLabel-label.Mui-active': { color: food.red },
                  '& .MuiStepLabel-label.Mui-completed': { color: '#22c55e' },
                  '& .MuiStepIcon-root': { color: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' },
                  '& .MuiStepIcon-root.Mui-active': { color: food.red },
                  '& .MuiStepIcon-root.Mui-completed': { color: '#22c55e' },
                  '& .MuiStepConnector-line': { borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
                }}>
                  <Step><StepLabel>Business Info</StepLabel></Step>
                  <Step><StepLabel>WhatsApp OTP</StepLabel></Step>
                  <Step><StepLabel>Admin Account</StepLabel></Step>
                </Stepper>
              </Box>
            )}

            {/* Heading */}
            {activeStep < 3 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
                  <Box sx={{ width: 3, height: 38, borderRadius: '2px', background: activeStep === 1 ? '#25d366' : food.red, mr: 2, mt: 0.5, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: n.textPrimary, fontFamily: 'Space Grotesk', letterSpacing: '-0.03em', mb: 0.3 }}>
                      {activeStep === 0 ? 'Your Restaurant' : activeStep === 1 ? 'WhatsApp Verification' : 'Create Admin Account'}
                    </Typography>
                    <Typography sx={{ color: n.textSecondary, fontSize: '0.88rem' }}>
                      {activeStep === 0 ? 'Tell us about your restaurant' : activeStep === 1 ? `Confirm the OTP sent to ${step1.phone}` : 'Set up your admin login credentials'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <Alert severity="error" sx={{ mb: 2.5, borderRadius: '14px', fontSize: '0.84rem' }}>{error}</Alert>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── STEP 0: Business Info ──────────────────────────────────── */}
            <AnimatePresence mode="wait">
              {activeStep === 0 && (
                <motion.div key="step0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.28 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}>
                    <TextField
                      label="Restaurant Name *"
                      value={step1.restaurantName}
                      onChange={e => handleNameChange(e.target.value)}
                      fullWidth
                      InputProps={{ startAdornment: <InputAdornment position="start"><Store sx={{ fontSize: 20 }} /></InputAdornment> }}
                      sx={inputSx}
                    />
                    <TextField
                      label="Restaurant Code *"
                      value={step1.restaurantCode}
                      onChange={e => setStep1(s => ({ ...s, restaurantCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                      fullWidth
                      helperText="This code is used to login. Auto-generated from your name."
                      InputProps={{ startAdornment: <InputAdornment position="start"><Business sx={{ fontSize: 20 }} /></InputAdornment> }}
                      FormHelperTextProps={{ sx: { color: n.textMuted, fontSize: '0.74rem' } }}
                      sx={inputSx}
                    />

                    <TextField
                      label="WhatsApp Phone Number *"
                      value={step1.phone}
                      onChange={e => setStep1(s => ({ ...s, phone: e.target.value }))}
                      fullWidth
                      helperText="Enter your country code (e.g. +91 for India) followed by number."
                      FormHelperTextProps={{ sx: { color: n.textMuted, fontSize: '0.74rem' } }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 20 }} /></InputAdornment> }}
                      sx={inputSx}
                    />
                    <TextField
                      label="Address (optional)"
                      value={step1.address}
                      onChange={e => setStep1(s => ({ ...s, address: e.target.value }))}
                      fullWidth
                      InputProps={{ startAdornment: <InputAdornment position="start"><Restaurant sx={{ fontSize: 20 }} /></InputAdornment> }}
                      sx={inputSx}
                    />

                    <Button
                      onClick={handleNextToOtp}
                      variant="contained" fullWidth
                      disabled={otpLoading}
                      endIcon={otpLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <ArrowForward />}
                      sx={{
                        py: 1.6, borderRadius: '9999px', fontWeight: 700, fontSize: '1rem', mt: 0.5,
                        background: `linear-gradient(135deg, ${food.red} 0%, ${food.redLight} 100%)`,
                        boxShadow: `0 6px 24px ${food.redGlow}`,
                        '&:hover': { background: `linear-gradient(135deg, ${food.redDark} 0%, ${food.red} 100%)`, transform: 'translateY(-2px)', boxShadow: `0 10px 32px ${food.redGlow}` },
                        '&.Mui-disabled': { opacity: 0.7 },
                      }}
                    >
                      {otpLoading ? 'Sending OTP...' : 'Send WhatsApp OTP'}
                    </Button>

                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: n.textMuted, fontSize: '0.82rem' }}>
                        Already have an account?{' '}
                        <Box component={Link} to="/login" sx={{ color: food.red, fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                          Sign In
                        </Box>
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              )}

              {/* ── STEP 1: WhatsApp OTP Verification ─────────────────────── */}
              {activeStep === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.28 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.2, alignItems: 'stretch' }}>
                    
                    {/* Visual Green Shield / Icon */}
                    <Box sx={{ 
                      alignSelf: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 76,
                      height: 76,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                      boxShadow: '0 8px 24px rgba(37,211,102,0.3)',
                      mb: 1
                    }}>
                      <Message sx={{ color: '#fff', fontSize: 36 }} />
                    </Box>

                    <Typography variant="body2" align="center" sx={{ color: n.textSecondary, px: 2 }}>
                      We sent a <strong>6-digit validation code</strong> directly to your WhatsApp account. Please open WhatsApp and enter the code below.
                    </Typography>

                    <TextField
                      label="Enter 6-Digit OTP *"
                      value={otp}
                      onChange={e => {
                        const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                        setOtp(val);
                        if (val.length === 6) {
                          handleVerifyOtp(val);
                        }
                      }}
                      autoFocus
                      disabled={otpLoading || otpVerified}
                      fullWidth
                      inputProps={{ 
                        style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em', fontWeight: 700 },
                        maxLength: 6
                      }}
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start"><VerifiedUser sx={{ color: '#25d366' }} /></InputAdornment> 
                      }}
                      sx={inputSx}
                    />

                    {otpVerified && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: '#22c55e', fontWeight: 700, mt: 1 }}>
                        <CheckCircle />
                        <Typography>OTP Verified! Redirecting...</Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Typography variant="caption" sx={{ color: n.textMuted }}>
                        {timerActive ? `Resend available in ${countdown}s` : 'You can resend the code now'}
                      </Typography>

                      <Button
                        size="small"
                        onClick={handleResendOtp}
                        disabled={timerActive || otpLoading || otpVerified}
                        startIcon={<Refresh />}
                        sx={{
                          color: '#25D366',
                          fontWeight: 700,
                          '&:hover': { background: 'rgba(37,211,102,0.08)' },
                          '&.Mui-disabled': { color: n.textMuted }
                        }}
                      >
                        Resend OTP
                      </Button>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                      <Button
                        onClick={handleBackToStep0}
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        disabled={otpLoading || otpVerified}
                        sx={{
                          py: 1.6, borderRadius: '9999px', fontWeight: 600, fontSize: '0.9rem', flex: 1,
                          borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(200,185,170,0.4)',
                          color: n.textSecondary,
                          '&:hover': { borderColor: food.red, color: food.red, background: food.redSoft },
                        }}
                      >
                        Change Phone
                      </Button>

                      <Button
                        onClick={() => handleVerifyOtp(otp)}
                        variant="contained"
                        disabled={otp.length !== 6 || otpLoading || otpVerified}
                        sx={{
                          py: 1.6, borderRadius: '9999px', fontWeight: 700, fontSize: '1rem', flex: 1.5,
                          background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                          boxShadow: '0 6px 20px rgba(37,211,102,0.3)',
                          '&:hover': { background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)', transform: 'translateY(-1px)' },
                        }}
                      >
                        {otpLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Verify Code'}
                      </Button>
                    </Box>
                  </Box>
                </motion.div>
              )}

              {/* ── STEP 2: Admin Account ──────────────────────────────────── */}
              {activeStep === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.28 }}>
                  <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <TextField label="First Name *" value={step2.firstName} onChange={e => setStep2(s => ({ ...s, firstName: e.target.value }))} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ fontSize: 20 }} /></InputAdornment> }} sx={inputSx} />
                      <TextField label="Last Name *" value={step2.lastName} onChange={e => setStep2(s => ({ ...s, lastName: e.target.value }))} fullWidth sx={inputSx} />
                    </Box>
                    <TextField
                      label="Admin Email *"
                      type="email"
                      value={step2.adminEmail}
                      onChange={e => setStep2(s => ({ ...s, adminEmail: e.target.value }))}
                      fullWidth
                      InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: 20 }} /></InputAdornment> }}
                      sx={inputSx}
                    />
                    <TextField
                      label="Username *"
                      value={step2.username}
                      onChange={e => setStep2(s => ({ ...s, username: e.target.value.toLowerCase().replace(/\s/g, '') }))}
                      fullWidth
                      helperText="Used to login. Min 4 characters, no spaces."
                      FormHelperTextProps={{ sx: { color: n.textMuted, fontSize: '0.74rem' } }}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ fontSize: 20 }} /></InputAdornment> }}
                      sx={inputSx}
                    />
                    <Box>
                      <TextField
                        label="Password *"
                        type={showPassword ? 'text' : 'password'}
                        value={step2.password}
                        onChange={e => setStep2(s => ({ ...s, password: e.target.value }))}
                        fullWidth
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Lock sx={{ fontSize: 20 }} /></InputAdornment>,
                          endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPassword(p => !p)} sx={{ color: n.textMuted }}>{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment>,
                        }}
                        sx={inputSx}
                      />
                      {step2.password && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(pwStrength.score / 4) * 100}
                            sx={{
                              height: 4, borderRadius: 2,
                              bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                              '& .MuiLinearProgress-bar': { bgcolor: pwStrength.color, borderRadius: 2 },
                            }}
                          />
                          <Typography sx={{ fontSize: '0.72rem', color: pwStrength.color, mt: 0.5, fontWeight: 600 }}>
                            {pwStrength.label}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <TextField
                      label="Confirm Password *"
                      type={showConfirm ? 'text' : 'password'}
                      value={step2.confirmPassword}
                      onChange={e => setStep2(s => ({ ...s, confirmPassword: e.target.value }))}
                      fullWidth
                      error={!!step2.confirmPassword && step2.password !== step2.confirmPassword}
                      helperText={step2.confirmPassword && step2.password !== step2.confirmPassword ? 'Passwords do not match' : ''}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Lock sx={{ fontSize: 20 }} /></InputAdornment>,
                        endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowConfirm(p => !p)} sx={{ color: n.textMuted }}>{showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment>,
                      }}
                      sx={inputSx}
                    />

                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                      <Button
                        onClick={handleBackToOtp}
                        variant="outlined" startIcon={<ArrowBack />}
                        sx={{
                          py: 1.6, borderRadius: '9999px', fontWeight: 600, fontSize: '0.9rem', flex: 0.4,
                          borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(200,185,170,0.4)',
                          color: n.textSecondary,
                          '&:hover': { borderColor: food.red, color: food.red, background: food.redSoft },
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                          py: 1.6, borderRadius: '9999px', fontWeight: 700, fontSize: '1rem', flex: 1,
                          background: `linear-gradient(135deg, ${food.red} 0%, ${food.redLight} 100%)`,
                          boxShadow: `0 6px 24px ${food.redGlow}`,
                          '&:hover': { background: `linear-gradient(135deg, ${food.redDark} 0%, ${food.red} 100%)`, transform: 'translateY(-2px)' },
                          '&.Mui-disabled': { opacity: 0.6 },
                        }}
                      >
                        {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : '🚀 Create Restaurant'}
                      </Button>
                    </Box>
                  </Box>
                </motion.div>
              )}

              {/* ── STEP 3: Success ────────────────────────────────────────── */}
              {activeStep === 3 && (
                <motion.div key="step3" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: [0.16,1,0.3,1] }}>
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}>
                      <Box sx={{
                        width: 90, height: 90, borderRadius: '50%', mx: 'auto', mb: 3,
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 12px 40px rgba(34,197,94,0.4)',
                        animation: 'maya-pulse-ring 2s ease-in-out infinite',
                      }}>
                        <CheckCircle sx={{ fontSize: 50, color: '#fff' }} />
                      </Box>
                    </motion.div>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.5 }}>
                      <CelebrationOutlined sx={{ color: food.red, fontSize: 28 }} />
                      <Typography variant="h5" sx={{ fontWeight: 800, color: n.textPrimary, fontFamily: 'Space Grotesk' }}>
                        You're all set!
                      </Typography>
                    </Box>
                    <Typography sx={{ color: n.textSecondary, mb: 3, fontSize: '0.9rem', lineHeight: 1.6 }}>
                      Your restaurant <strong>{step1.restaurantName}</strong> is ready.<br />
                      Your 7-day Demo plan starts now.
                    </Typography>

                    {/* Credentials Box */}
                    <Box sx={{
                      p: 2.5, borderRadius: '20px', mb: 3, textAlign: 'left',
                      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(224,42,42,0.04)',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(224,42,42,0.15)'}`,
                    }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: food.red, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
                        🔑 Your Login Credentials
                      </Typography>
                      {[
                        { label: 'Restaurant Code', value: createdRestaurantCode || step1.restaurantCode.toUpperCase() },
                        { label: 'Username', value: step2.username },
                        { label: 'Password', value: '(your chosen password)' },
                      ].map(({ label, value }) => (
                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`, '&:last-child': { borderBottom: 'none' } }}>
                          <Typography sx={{ fontSize: '0.8rem', color: n.textMuted }}>{label}</Typography>
                          <Chip label={value} size="small" sx={{ fontWeight: 700, fontSize: '0.78rem', bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(224,42,42,0.08)', color: isDark ? '#fff' : food.red, border: 'none' }} />
                        </Box>
                      ))}
                    </Box>

                    <Button
                      variant="contained" fullWidth
                      onClick={() => navigate('/dashboard')}
                      sx={{
                        py: 1.7, borderRadius: '9999px', fontWeight: 700, fontSize: '1.02rem',
                        background: `linear-gradient(135deg, ${food.red} 0%, ${food.redLight} 100%)`,
                        boxShadow: `0 8px 28px ${food.redGlow}`,
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 36px ${food.redGlow}` },
                      }}
                    >
                      Go to Dashboard →
                    </Button>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer security badges */}
            {activeStep < 3 && (
              <Box sx={{ display: 'flex', gap: 1, mt: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                {['WhatsApp Verified', '7-Day Free Trial', 'No Card Required'].map(badge => (
                  <Chip key={badge} label={badge} size="small" sx={{ fontSize: '0.68rem', borderRadius: '9999px', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(200,185,170,0.3)'}`, color: n.textMuted, boxShadow: 'none' }} />
                ))}
              </Box>
            )}
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default RegisterPage;
